"use client";
import React, { useState, useMemo, useEffect } from 'react';
import {
  Search, Filter, Plus, ChevronDown,
  Users, Edit3, Trash2, Eye, EyeOff,
  Mail, Phone, MapPin,
} from 'lucide-react';
import CustomerModal from './CustomerModal';
import { useCustomers } from "../context/CustomersContext";
import { useOrders } from "../context/OrdersContext";
import { useCustomerTypes } from "../context/CustomerTypesContext";
import { useLocation } from "react-router-dom";
const CustomersPage = () => {
  const { customers, loading, error, addCustomer, updateCustomer, deleteCustomer, toggleCustomerStatus } = useCustomers();
  const { orders, fetchOrders } = useOrders();
  const { customerTypes } = useCustomerTypes();

  useEffect(() => { fetchOrders(); }, []);

  const CUSTOMER_TYPE_RULES = {
    vip:       { minSpent: 100000, minOrders: 50 },
    premium:   { minSpent: 50000,  minOrders: 20 },
    wholesale: { minSpent: 20000,  minOrders: 30 },
  };

  /* ── Compute per-customer stats from orders ── */
  const customerStats = useMemo(() => {
    const stats = {};
    customers.forEach(c => {
      stats[c.id] = { totalOrders: 0, totalSpent: 0, lastOrderDate: null, customerType: 'regular' };
    });
    orders.forEach(order => {
      const id = order.customer?.id;
      if (!id || !stats[id]) return;
      stats[id].totalOrders += 1;
      stats[id].totalSpent += Number(order.total || 0);
      if (!stats[id].lastOrderDate || new Date(order.date) > new Date(stats[id].lastOrderDate)) {
        stats[id].lastOrderDate = order.date;
      }
    });
    Object.keys(stats).forEach(id => {
      const { totalSpent, totalOrders } = stats[id];
      if (totalSpent >= CUSTOMER_TYPE_RULES.vip.minSpent || totalOrders >= CUSTOMER_TYPE_RULES.vip.minOrders)
        stats[id].customerType = 'vip';
      else if (totalSpent >= CUSTOMER_TYPE_RULES.premium.minSpent || totalOrders >= CUSTOMER_TYPE_RULES.premium.minOrders)
        stats[id].customerType = 'premium';
      else if (totalSpent >= CUSTOMER_TYPE_RULES.wholesale.minSpent || totalOrders >= CUSTOMER_TYPE_RULES.wholesale.minOrders)
        stats[id].customerType = 'wholesale';
    });
    return stats;
  }, [customers, orders]);

  /* ── State ── */

const location = useLocation();
const [searchTerm, setSearchTerm] = useState(location.state?.prefillSearch || "");
  const [statusFilter, setStatusFilter]             = useState('all');
  const [customerTypeFilter, setCustomerTypeFilter] = useState('all');
  const [sortBy, setSortBy]                         = useState('name');
  const [sortOrder, setSortOrder]                   = useState('asc');
  const [showFilters, setShowFilters]               = useState(false);
  const [selectedCustomers, setSelectedCustomers]   = useState([]);
  const [showAddModal, setShowAddModal]             = useState(false);
  const [editingCustomer, setEditingCustomer]       = useState(null);
  const [currentPage, setCurrentPage]               = useState(1);
  const [itemsPerPage, setItemsPerPage]             = useState(10);

  /* ── Options ── */
  const statusOptions = [
    { value: 'all',      label: 'All Customers', count: customers.length },
    { value: 'active',   label: 'Active',        count: customers.filter(c => c.status === 'active').length },
    { value: 'inactive', label: 'Inactive',      count: customers.filter(c => c.status === 'inactive').length },
  ];

  const customerTypeOptions = useMemo(() => [
    { value: 'all', label: 'All Types' },
    ...customerTypes.map(type => ({ value: type.type.toLowerCase(), label: type.type })),
  ], [customerTypes]);

  const sortOptions = [
    { value: 'name',             label: 'Name'              },
    { value: 'email',            label: 'Email'             },
    { value: 'registrationDate', label: 'Registration Date' },
    { value: 'lastOrderDate',    label: 'Last Order'        },
    { value: 'totalOrders',      label: 'Total Orders'      },
    { value: 'totalSpent',       label: 'Total Spent'       },
  ];

  /* ── Filtered + sorted list ── */
  const filteredAndSortedCustomers = useMemo(() => {
    let list = customers.filter(c => {
      const s = searchTerm.toLowerCase();
      const matchSearch = !searchTerm ||
        c.fullName.toLowerCase().includes(s) ||
        c.email.toLowerCase().includes(s) ||
        c.phone.includes(searchTerm) ||
        c.address.city.toLowerCase().includes(s);
      const matchStatus = statusFilter === 'all' || c.status === statusFilter;
      const matchType   = customerTypeFilter === 'all' || c.customerType?.toLowerCase() === customerTypeFilter;
      return matchSearch && matchStatus && matchType;
    });

    list.sort((a, b) => {
      let av, bv;
      if (sortBy === 'name')             { av = a.fullName?.toLowerCase() || '';              bv = b.fullName?.toLowerCase() || ''; }
      else if (sortBy === 'totalOrders') { av = customerStats[a.id]?.totalOrders || 0;        bv = customerStats[b.id]?.totalOrders || 0; }
      else if (sortBy === 'totalSpent')  { av = customerStats[a.id]?.totalSpent  || 0;        bv = customerStats[b.id]?.totalSpent  || 0; }
      else if (sortBy === 'lastOrderDate') {
        av = new Date(customerStats[a.id]?.lastOrderDate || 0);
        bv = new Date(customerStats[b.id]?.lastOrderDate || 0);
      } else { av = a[sortBy]; bv = b[sortBy]; }
      return sortOrder === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });
    return list;
  }, [customers, searchTerm, statusFilter, customerTypeFilter, sortBy, sortOrder, customerStats]);

  const paginatedCustomers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedCustomers.slice(start, start + itemsPerPage);
  }, [filteredAndSortedCustomers, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredAndSortedCustomers.length / itemsPerPage);

  const stats = useMemo(() => {
    const totalRevenue  = Object.values(customerStats).reduce((s, x) => s + (x.totalSpent  || 0), 0);
    const totalOrderSum = Object.values(customerStats).reduce((s, x) => s + (x.totalOrders || 0), 0);
    return {
      totalCustomers:  customers.length,
      activeCustomers: customers.filter(c => c.status === 'active').length,
      totalRevenue,
      avgOrderValue: totalOrderSum > 0 ? Math.round(totalRevenue / totalOrderSum) : 0,
    };
  }, [customers, customerStats]);

  /* ── Handlers ── */
  const handleSelectCustomer = id =>
    setSelectedCustomers(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const handleSelectAll = () =>
    setSelectedCustomers(
      selectedCustomers.length === paginatedCustomers.length && paginatedCustomers.length > 0
        ? [] : paginatedCustomers.map(c => c.id)
    );

  const handleDeleteCustomer = id => {
    if (window.confirm('Delete this customer?')) {
      deleteCustomer(id);
      setSelectedCustomers(p => p.filter(x => x !== id));
    }
  };

  const handleEditCustomer  = c  => { setEditingCustomer(c);    setShowAddModal(true); };
  const handleToggleStatus  = id => { toggleCustomerStatus(id); };
  const handleAddCustomer   = () => { setEditingCustomer(null); setShowAddModal(true); };

  const handleSaveCustomer = async (data) => {
    try {
      const formData = new FormData();
      formData.append('first_name',    data.firstName    || '');
      formData.append('last_name',     data.lastName     || '');
      formData.append('email',         data.email        || '');
      formData.append('phone',         data.phone        || '');
      formData.append('password',      data.password     || '');
      formData.append('date_of_birth', data.dateOfBirth  || '');
      formData.append('gender',        data.gender       || '');
      formData.append('status',        data.status       || 'active');
      formData.append('customer_type', data.customerType || '');
      formData.append('address',       data.address?.street  || '');
      formData.append('city',          data.address?.city    || '');
      formData.append('state',         data.address?.state   || '');
      formData.append('pincode',       data.address?.pincode || '');
      formData.append('country',       data.address?.country || '');
      formData.append('notes',         data.notes        || '');
      if (data.profileImageFile) formData.append('image', data.profileImageFile);

      if (editingCustomer) await updateCustomer(editingCustomer.id, formData);
      else                 await addCustomer(formData);

      setEditingCustomer(null);
      setShowAddModal(false);
    } catch (err) {
      console.error('Save customer error:', err);
    }
  };

  const selectedData = useMemo(() => customers.filter(c => selectedCustomers.includes(c.id)), [customers, selectedCustomers]);
  const allActive    = selectedData.length > 0 && selectedData.every(c => c.status === 'active');

  const handleActivateSelected = async () => {
    for (const c of selectedData) if (c.status !== 'active')   await updateCustomer(c.id, { ...c, status: 'active' });
    setSelectedCustomers([]);
  };
  const handleDeactivateSelected = async () => {
    for (const c of selectedData) if (c.status !== 'inactive') await updateCustomer(c.id, { ...c, status: 'inactive' });
    setSelectedCustomers([]);
  };
  const handleDeleteSelected = async () => {
    if (!window.confirm('Delete selected customers?')) return;
    for (const id of selectedCustomers) await deleteCustomer(id);
    setSelectedCustomers([]);
  };

  const handleSort = field => {
    if (sortBy === field) setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortOrder('asc'); }
  };

  /* ── Pagination helper ── */
  const getPaginationPages = () => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (currentPage <= 3)                return [1, 2, 3, 4, '...', totalPages];
    if (currentPage >= totalPages - 2)   return [1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
  };

  /* ── Helpers ── */
  const getTypeColor = (type) => {
    const colors = [
      'bg-purple-100 text-purple-800',
      'bg-blue-100 text-blue-800',
      'bg-gray-100 text-orange-800',
      'bg-green-100 text-green-800',
      'bg-pink-100 text-pink-800',
    ];
    const index = customerTypes.findIndex(t => t.type.toLowerCase() === type);
    return colors[index % colors.length] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = s => s === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';

  const SortArrow = ({ field }) => sortBy === field
    ? <span className="text-teal-600 ml-0.5">{sortOrder === 'asc' ? '↑' : '↓'}</span>
    : null;

  /* ── Inline action buttons (replaces the old "⋮" dropdown) ── */
  const ActionDropdown = ({ customer }) => (
    <div className="flex items-center gap-1">
      <button
        onClick={e => { e.stopPropagation(); handleEditCustomer(customer); }}
        title="Edit"
        className="p-1.5 hover:bg-teal-50 rounded-lg transition-colors group"
      >
        <Edit3 className="w-4 h-4 text-gray-400 group-hover:text-teal-600" />
      </button>
      <button
        onClick={e => { e.stopPropagation(); handleToggleStatus(customer.id); }}
        title={customer.status === 'active' ? 'Deactivate' : 'Activate'}
        className="p-1.5 hover:bg-amber-50 rounded-lg transition-colors group"
      >
       {customer.status === "active" ? (
  <Eye className="w-4 h-4 text-green-600" />
) : (
  <EyeOff className="w-4 h-4 text-red-600" />
)}
      </button>
      <button
        onClick={e => { e.stopPropagation(); handleDeleteCustomer(customer.id); }}
        title="Delete"
        className="p-1.5 hover:bg-red-50 rounded-lg transition-colors group"
      >
        <Trash2 className="w-4 h-4 text-gray-400 group-hover:text-red-600" />
      </button>
    </div>
  );

  /* ── Empty state ── */
 const EmptyState = () => (
  <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
    <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
      <Users className="w-8 h-8 text-gray-300" />
    </div>
    <h3 className="text-lg font-semibold text-gray-700 mb-1">No customers found</h3>
    <p className="text-sm text-gray-400 mb-5 max-w-xs">
      {searchTerm || statusFilter !== 'all' || customerTypeFilter !== 'all'
        ? 'Try adjusting your search or filters.'
        : 'Get started by adding your first customer.'}
    </p>
    {searchTerm || statusFilter !== 'all' || customerTypeFilter !== 'all' ? (
      <button
        onClick={() => {
          setSearchTerm('');
          setStatusFilter('all');
          setCustomerTypeFilter('all');
          setCurrentPage(1);
        }}
        className="px-5 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
      >
        Clear filters
      </button>
    ) : (
      <button
        onClick={handleAddCustomer}
        className="inline-flex items-center gap-2 bg-teal-600 text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-teal-700 transition-colors"
      >
        <Plus className="w-4 h-4" /> Add Customer
      </button>
    )}
  </div>
);
  /* ── Pagination footer ── */
  const PaginationFooter = () => (
    <div className="px-4 py-3.5 border-t border-gray-100 bg-gray-50 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
      <div className="flex items-center gap-2 text-sm text-gray-500 flex-wrap">
        <span>Per page:</span>
        <select
          value={itemsPerPage}
          onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
          className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
        >
          {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        {filteredAndSortedCustomers.length > 0 && (
          <span className="text-gray-400">
            {(currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, filteredAndSortedCustomers.length)} of {filteredAndSortedCustomers.length}
          </span>
        )}
      </div>
      {totalPages > 1 && (
        <div className="flex items-center gap-1 flex-wrap">
          <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}
            className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors">«</button>
          <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}
            className="px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-medium">‹</button>
          <span className="sm:hidden px-3 py-1.5 text-sm text-gray-600 font-medium">{currentPage} / {totalPages}</span>
          <div className="hidden sm:flex items-center gap-1">
            {getPaginationPages().map((page, i) =>
              page === '...'
                ? <span key={`e${i}`} className="px-2 text-gray-400 text-sm select-none">…</span>
                : <button key={page} onClick={() => setCurrentPage(page)}
                    className={`min-w-[34px] px-2 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      currentPage === page ? 'bg-teal-600 text-white' : 'border border-gray-300 text-gray-700 hover:bg-white'
                    }`}>{page}</button>
            )}
          </div>
          <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}
            className="px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-medium">›</button>
          <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}
            className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors">»</button>
        </div>
      )}
    </div>
  );

  /* ════════════════════════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════════════════════════ */
  return (
    /*
     * Centered with a max width (matches the Mandals/States/Districts/Cities
     * pages) so there are gaps on both sides on large screens. The table
     * below still uses table-fixed with percentage column widths, so it
     * compresses/expands proportionally inside this narrower container
     * without overflowing.
     */
    <div className="max-w-[1200px] mx-auto px-3 sm:px-4 lg:px-6 py-4">

      {/* ── Stats cards — 2-col on mobile, 4-col on sm+ ── */}
      <section className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[
          { label:'Total Customers', value:stats.totalCustomers,                       accent:'border-l-teal-500',   color:'text-teal-700',   bg:'bg-teal-50',   icon:<Users className="w-4 h-4 text-teal-600" /> },
          { label:'Active',          value:stats.activeCustomers,                       accent:'border-l-green-500',  color:'text-green-600',  bg:'bg-green-50',  icon:<Eye   className="w-4 h-4 text-green-600" /> },
          { label:'Total Revenue',   value:`₹${stats.totalRevenue.toLocaleString()}`,   accent:'border-l-blue-400',   color:'text-blue-700',   bg:'bg-blue-50',   icon:<span className="text-blue-600 font-bold text-sm">₹</span> },
          { label:'Avg Order Value', value:`₹${stats.avgOrderValue.toLocaleString()}`,  accent:'border-l-purple-400', color:'text-purple-700', bg:'bg-purple-50', icon:<span className="text-purple-600 font-bold text-sm">Ø</span> },
        ].map(({ label, value, accent, color, bg, icon }) => (
          <div key={label} className={`bg-white rounded-xl border border-gray-100 border-l-4 ${accent} p-4 flex items-center gap-3 shadow-sm`}>
            <div className={`w-9 h-9 ${bg} rounded-lg flex items-center justify-center shrink-0`}>{icon}</div>
            <div className="min-w-0"><p className={`text-lg font-bold ${color} truncate`}>{value}</p><p className="text-xs text-gray-400 truncate">{label}</p></div>
          </div>
        ))}
      </section>

      {/* ── Search & Filters ── */}
      <section className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4 mb-4">

        {/* Search row */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
            <input
              type="text"
              placeholder="Search by name, email, phone, or city…"
              value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 bg-gray-50 placeholder:text-gray-400"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 px-3 py-2.5 border rounded-lg text-sm font-medium transition-colors shrink-0 ${
              showFilters ? 'border-teal-400 text-teal-700 bg-teal-50' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Filter className="w-4 h-4" />
            <span className="hidden sm:inline">Filters</span>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
          <button onClick={handleAddCustomer}
            className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2.5 rounded-xl font-semibold text-sm transition-colors shadow-sm shrink-0">
            <Plus className="w-4 h-4" /><span className="hidden sm:inline">Add Customer</span>
          </button>
        </div>

        {/* Expandable filters */}
        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4 pt-4 border-t border-gray-100">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Status</label>
              <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 bg-white">
                {statusOptions.map(o => <option key={o.value} value={o.value}>{o.label} ({o.count})</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Customer Type</label>
              <select value={customerTypeFilter} onChange={e => { setCustomerTypeFilter(e.target.value); setCurrentPage(1); }}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 bg-white">
                {customerTypeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Sort By</label>
              <div className="flex gap-2">
                <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 bg-white">
                  {sortOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <button onClick={() => setSortOrder(s => s === 'asc' ? 'desc' : 'asc')}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-bold text-gray-600 hover:bg-gray-50 transition-colors min-w-[40px]">
                  {sortOrder === 'asc' ? '↑' : '↓'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk actions */}
        {selectedCustomers.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <span className="text-sm font-medium text-gray-700">
                {selectedCustomers.length} customer{selectedCustomers.length !== 1 ? 's' : ''} selected
              </span>
              <div className="flex gap-2 flex-wrap">
                <button onClick={allActive ? handleDeactivateSelected : handleActivateSelected}
                  className={`flex-1 sm:flex-none px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    allActive ? 'bg-amber-50 text-amber-700 hover:bg-amber-100' : 'bg-green-50 text-green-700 hover:bg-green-100'
                  }`}>
                  {allActive ? 'Deactivate' : 'Activate'}
                </button>
                <button onClick={handleDeleteSelected}
                  className="flex-1 sm:flex-none px-3 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium">
                  Delete
                </button>
                <button onClick={() => setSelectedCustomers([])}
                  className="flex-1 sm:flex-none px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium">
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* ── Result count ── */}
      <div className="mb-3 px-0.5">
        <p className="text-sm text-gray-500">
          <span className="font-semibold text-gray-700">{filteredAndSortedCustomers.length}</span>{' '}
          customer{filteredAndSortedCustomers.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* ── Content ── */}
      <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">

        {/* ══ CARD VIEW — below lg ══ */}
        <div className="lg:hidden">
          {paginatedCustomers.length === 0 ? <EmptyState /> : (
            <div className="divide-y divide-gray-100">
              {paginatedCustomers.map(customer => {
                const cs = customerStats[customer.id] || {};
                return (
                  <div key={customer.id}
                    className={`p-4 transition-colors ${selectedCustomers.includes(customer.id) ? 'bg-teal-50' : 'hover:bg-gray-50'}`}>
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">{customer.fullName}</p>
                            <p className="text-xs text-gray-400 truncate">{customer.email}</p>
                          </div>
                          <ActionDropdown customer={customer} />
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                          <span className="flex items-center gap-1"><Phone className="w-3 h-3 shrink-0" />{customer.phone}</span>
                          <span className="flex items-center gap-1"><MapPin className="w-3 h-3 shrink-0" />{customer.address.city}, {customer.address.state}</span>
                        </div>
                        <div className="mt-2.5 flex flex-wrap items-center gap-2">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full capitalize ${getTypeColor(cs.customerType)}`}>
                            {customer.customer_type?.type || customer.customer_type || 'Regular'}
                          </span>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full capitalize ${getStatusColor(customer.status)}`}>
                            {customer.status}
                          </span>
                          <span className="text-xs text-gray-400 ml-auto whitespace-nowrap">
                            {cs.totalOrders || 0} orders · ₹{(cs.totalSpent || 0).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <PaginationFooter />
        </div>

        {/* ══ TABLE VIEW — lg+ ══
            Table uses auto layout so columns compress naturally to fit the
            available content-area width. Each column has a sensible max/min
            to prevent text overflow.
        ══ */}
        <div className="hidden lg:block overflow-x-auto [&::-webkit-scrollbar]:h-[3px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full">
          <table className="w-full text-sm table-fixed">
            <thead className="bg-teal-700 text-white">
              <tr>
                {/* Column widths tuned to fill the content area */}
                <th className="px-3 py-3.5 text-left text-xs font-semibold text-white uppercase tracking-wider w-[60px]">ID</th>
                <th onClick={() => handleSort('name')}
                  className="px-3 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-teal-700 w-[18%] select-none">
                  Customer <SortArrow field="name" />
                </th>
                <th className="px-3 py-3.5 text-left text-xs font-semibold text-white uppercase tracking-wider w-[20%]">Contact</th>
                <th className="px-3 py-3.5 text-left text-xs font-semibold text-white uppercase tracking-wider w-[16%]">Location</th>
                <th onClick={() => handleSort('totalOrders')}
                  className="px-3 py-3.5 text-left text-xs font-semibold text-white uppercase tracking-wider w-[10%]">
                  Orders <SortArrow field="totalOrders" />
                </th>
                <th onClick={() => handleSort('totalSpent')}
                  className="px-3 py-3.5 text-left text-xs font-semibold text-white uppercase tracking-wider w-[10%]">
                  Spent <SortArrow field="totalSpent" />
                </th>
                <th className="px-3 py-3.5 text-left text-xs font-semibold text-white uppercase tracking-wider w-[10%]">Type</th>
                <th className="px-3 py-3.5 text-left text-xs font-semibold text-white uppercase tracking-wider w-[9%]">Status</th>
                <th className="px-3 py-3.5 text-left text-xs font-semibold text-white uppercase tracking-wider w-[100px]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginatedCustomers.length === 0
                ? <tr><td colSpan={9}><EmptyState /></td></tr>
                : paginatedCustomers.map(customer => {
                    const cs = customerStats[customer.id] || {};
                    return (
                      <tr key={customer.id}
                        className={`transition-colors ${selectedCustomers.includes(customer.id) ? 'bg-teal-50' : 'hover:bg-gray-50'}`}>

                        {/* ID */}
                        <td className="px-3 py-3.5 text-xs text-gray-400 font-mono truncate">{customer.id}</td>

                        {/* Customer name + email */}
                        <td className="px-3 py-3.5">
                          <p className="font-medium text-gray-900 truncate text-sm">{customer.fullName}</p>
                          <p className="text-xs text-gray-400 truncate">{customer.email}</p>
                        </td>

                        {/* Contact */}
                        <td className="px-3 py-3.5">
                          <p className="flex items-center gap-1 text-xs text-gray-700 truncate">
                            <Mail  className="w-3 h-3 text-gray-400 shrink-0" />{customer.email}
                          </p>
                          <p className="flex items-center gap-1 text-xs text-gray-500 mt-0.5 truncate">
                            <Phone className="w-3 h-3 shrink-0" />{customer.phone}
                          </p>
                        </td>

                        {/* Location */}
                        <td className="px-3 py-3.5">
                          <div className="flex items-center gap-1 text-xs text-gray-700 min-w-0">
                            <MapPin className="w-3 h-3 text-gray-400 shrink-0" />
                            <span className="truncate">{customer.address.city}, {customer.address.state}</span>
                          </div>
                          <p className="text-xs text-gray-400 mt-0.5 truncate">{customer.address.country}</p>
                        </td>

                        {/* Orders */}
                        <td className="px-3 py-3.5">
                          <p className="font-medium text-gray-900 text-sm">{cs.totalOrders || 0}</p>
                          <p className="text-xs text-gray-400">
                            {cs.lastOrderDate ? new Date(cs.lastOrderDate).toLocaleDateString() : 'Never'}
                          </p>
                        </td>

                        {/* Spent */}
                        <td className="px-3 py-3.5 font-medium text-gray-900 text-sm truncate">
                          ₹{(cs.totalSpent || 0).toLocaleString()}
                        </td>

                        {/* Type */}
                        <td className="px-3 py-3.5">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full capitalize ${getTypeColor(customer.customerType)}`}>
                            {customer.customerType || 'Regular'}
                          </span>
                        </td>

                        {/* Status */}
                        <td className="px-3 py-3.5">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full capitalize ${getStatusColor(customer.status)}`}>
                            {customer.status}
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="px-3 py-3.5">
                          <ActionDropdown customer={customer} />
                        </td>
                      </tr>
                    );
                  })
              }
            </tbody>
          </table>
          {paginatedCustomers.length > 0 && <PaginationFooter />}
        </div>
      </section>

      {/* ── Modal ── */}
      <CustomerModal
        isOpen={showAddModal}
        onClose={() => { setShowAddModal(false); setEditingCustomer(null); }}
        customer={editingCustomer}
        onSave={handleSaveCustomer}
      />
    </div>
  );
};

export default CustomersPage;